import { Card } from './Card';

/**
 * Loading placeholders that keep the page's shape.
 *
 * A spinner says "wait" and nothing else. A skeleton says what is coming and
 * how much of it, so the layout does not jump when the data lands and the same
 * wait reads as roughly half as long. CLAUDE.md's UX rules ban full-page
 * spinners for exactly this reason — these are what replaces them.
 *
 * Match the skeleton to the shape it stands in for: a table gets rows, a grid
 * of stat cards gets cards, a divided list gets lines.
 */

/** Rows inside a bordered card — for list and table pages. */
export function TableSkeleton({ rows = 6, columns = 4 }: { rows?: number; columns?: number }) {
  const widths = ['w-1/3', 'w-1/4', 'w-1/5', 'w-1/6', 'w-2/5', 'w-1/4'];
  return (
    <Card noPadding clip>
      <div className="h-10 border-b border-slate-200/80 bg-slate-50" />
      <div className="divide-y divide-slate-100">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex animate-pulse items-center gap-4 px-4 py-3.5">
            {Array.from({ length: columns }).map((_, c) => (
              <div
                key={c}
                className={`h-4 rounded bg-slate-200 ${
                  c === 0 ? 'flex-1' : `${widths[c % widths.length]} hidden md:block`
                }`}
              />
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
}

/** A grid of card placeholders — for dashboards and stat rows. */
export function CardSkeleton({ count = 4, className = '' }: { count?: number; className?: string }) {
  return (
    <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 ${className}`.trim()}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="animate-pulse">
          <div className="h-3 w-24 rounded bg-slate-200" />
          <div className="mt-3 h-7 w-16 rounded bg-slate-200" />
        </Card>
      ))}
    </div>
  );
}

/** Lines inside a single card — for panels, timelines and detail sections. */
export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <Card>
      <div className="animate-pulse space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="h-4 flex-1 rounded bg-slate-200" />
            <div className="h-5 w-16 shrink-0 rounded-full bg-slate-200" />
          </div>
        ))}
      </div>
    </Card>
  );
}

const SPINNER_SIZES = {
  sm: 'h-4 w-4 border-2',
  md: 'h-6 w-6 border-2',
  lg: 'h-8 w-8 border-4',
} as const;

/**
 * The one place a spinner is still right: work the user just started that has
 * no shape to preview — a save, an export, a connection test. Never for a page
 * or a list; use a skeleton there.
 */
export function InlineSpinner({
  label,
  size = 'sm',
  className = '',
}: {
  label?: string;
  size?: keyof typeof SPINNER_SIZES;
  className?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-2 text-sm text-slate-600 ${className}`.trim()} role="status">
      <span
        className={`${SPINNER_SIZES[size]} shrink-0 animate-spin rounded-full border-slate-200 border-t-[#007ACC]`}
      />
      {label}
    </span>
  );
}
